import { equipments } from "@/api/equipment/useEquipmentQuery";
import { MonitoringBox } from "@/features/monitoring/components/parentsBox/Monitoring";
import { Parents } from "@/features/monitoring/components/parentsBox/Parents";
import { Main } from "@/features/standard/Main";
import { BASE_URL } from "@/instance/axios";
import {
  getLowerFields,
  getTextFields,
  getUpperFields,
  getWorkStatusFields,
} from "@/utils/monitoringFieldUtils";
import { Flex, MultiSelect, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSocket } from "socket.io-react-hook";

export type MonitoringDataProps = {
  denominatorLoad?: number;
  equipmentGroupKey?: string;
  equipmentCode?: string;
  equipmentName?: string;
  errorName?: string;
  errorNumber?: number;
  idleTime?: string;
  idleTimeCreatedAt?: Date;
  influxEquipmentCode?: string;
  itemCode?: string;
  itemName?: string;
  itemSpec?: string;
  keyCamStatus?: string;
  keyCamStatusNumber?: number;
  limitCounter?: number;
  loadRatio?: number | undefined;
  moldCavity?: number | undefined;
  moldCode?: string;
  msPerUnit?: number;
  numeratorLoad?: number;
  presetCounter?: number;
  pressStatus?: string;
  pressStatusNumber?: number | undefined;
  remainTime?: string;
  runTime?: string;
  runTimeCreatedAt?: Date;
  runTimeRatio?: number;
  spm?: number;
  totalCounter?: number;
  totalTon?: number;
  worker?: string;
  equipmentType?: "FORMING_01" | "FORMING_02" | "PACKING" | "FORGING";
  packingCounter?: number;
  lotId?: number;
  quantity?: string;
  targetQuantity?: string;
  kgPerCount?: string;
  totalPackingCounter?: string;
  workingTime?: string;
  productionRate?: string;
  trackingStatus?: string;

  mainMotorRpm?: string;
  volFeederRpm?: string;
  mainFeederRpm?: number;
  sideFeederRpm?: number;
  screwRpm?: number;
  vacuumRpm?: number;
  vacuumPressure?: number;
  meltTemperature?: number;
  meltPressure?: number;

  lotName?: string;

  itemWithGrade?: {
    abstractItemCode: string;
    code: string;
    gradeName: string;
    id: number;
    itemCategoryName: string;
    itemType: string;
    name: string;
    unitText: string;
  };
};

const Monitoring = Object.assign({}, Main, { Parents });

const useSocketListeners = (
  equipmentCodes: string[] | undefined,
  socket: any,
  setMonitoringData: React.Dispatch<React.SetStateAction<MonitoringDataProps[]>>,
) => {
  useEffect(() => {
    if (!equipmentCodes) return;

    equipmentCodes.forEach((code, index) => {
      const roomName = `PM_${code}`;
      socket.on(roomName, (message: MonitoringDataProps) => {
        setMonitoringData((prevData) => {
          const newData = [...prevData];
          newData[index] = message;
          return newData;
        });
      });
      socket.emit("call", "socket.join", { room: roomName }, (err: any) => {
        if (err) console.error(err);
      });
    });

    return () => {
      equipmentCodes.forEach((code) => {
        const roomName = `PM_${code}`;
        socket.off(roomName);
      });
    };
  }, [socket, equipmentCodes, setMonitoringData]);
};

export const MonitoringTable = () => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [multiSelectData, setMultiSelectData] = useState<{ value: string; label: string }[]>([]);
  const [monitoringData, setMonitoringData] = useState<MonitoringDataProps[]>([]);
  const { socket } = useSocket(BASE_URL, { autoConnect: true });

  const { data: equipmentData } = useQuery({
    ...equipments?.find({
      query: {
        $and: [
          {
            influxEquipmentCode: {
              $and: [
                { $not: null },
                { $not: "" },
                { $or: selectedOptions ? selectedOptions.map((option) => ({ $eq: option })) : [] },
              ],
            },
          },
        ],
      },
      sort: "seq, id",
      populate: ["location", "works"],
    }),
  });

  const equipmentCodes = equipmentData?.data?.map((data) => data?.code);

  useSocketListeners(equipmentCodes, socket, setMonitoringData);

  useEffect(() => {
    if (multiSelectData.length === 0 && equipmentData) {
      setMultiSelectData(
        equipmentData?.data?.map((data) => ({
          value: data.influxEquipmentCode as string,
          label: data.name,
        })) || [],
      );
    }
  }, [equipmentData]);

  const handleMultiSelectChange = (values: string[]) => {
    setSelectedOptions(values);
    setMonitoringData([]);
  };

  const monitoringGroupData = Object.entries(
    monitoringData.reduce(
      (acc, item) => {
        const groupKey = item?.equipmentGroupKey || "기타";
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(item);
        return acc;
      },
      {} as Record<string, MonitoringDataProps[]>,
    ),
  ).map(([groupKey, items]) => ({
    equipmentGroupKey: groupKey,
    items,
  }));

  const mergedItems = monitoringGroupData.map((group) => {
    const [first, second] = group.items;

    if (!second) {
      return {
        equipmentGroupKey: group.equipmentGroupKey,
        items: [first],
      };
    }

    // 제외할 키 목록
    const excludeKeys = ["equipmentName", "equipmentType"];

    // 제외한 나머지만 추출해서 병합
    const filteredSecond = Object.fromEntries(
      Object.entries(second).filter(([key]) => !excludeKeys.includes(key)),
    );

    const merged = { ...first, ...filteredSecond };

    return {
      equipmentGroupKey: group.equipmentGroupKey,
      items: [merged, second], // second도 그대로 유지한다면 포함
    };
  });

  const realTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm");
  return (
    <>
      <Flex direction="row" gap="1rem" align="center">
        <MultiSelect
          data={multiSelectData ?? []}
          placeholder="검색할 기계를 선택해주세요."
          onChange={handleMultiSelectChange}
        />
        <Text>현재시간 : {realTime}</Text>
      </Flex>
      <Monitoring.Parents>
        {mergedItems.map((data, index) => {
          return (
            <div>
              {data.items.map((item, index) => (
                <MonitoringBox
                  key={index}
                  statusInfo={{
                    pressStatus: item?.pressStatus,
                    errorName: item?.errorName,
                  }}
                  textFields={getTextFields(item)}
                  upperFields={getUpperFields(item)}
                  lowerFields={getLowerFields(item)}
                  workStatusFields={getWorkStatusFields(item)}
                  backgroundColor={index ? "#c5ddf2" : "gainsboro"}
                />
              ))}
            </div>
          );
        })}
      </Monitoring.Parents>
    </>
  );
};
